import React from 'react';
import {observer} from 'mobx-react';
import {reaction} from 'mobx';
import {PropTypes} from 'prop-types';
import {Dropzone} from '../dropzone/dropzone.jsx';
import {DatePicker} from '../date-picker/date-picker.jsx';
import config from 'config';
import TextInput from '../forms/text-input';
import RadioGroup from '../forms/radio-group';
import AppFormCheckboxList from '../../pages/parts/_app-form-checkbox-list.jsx';
import AppFormGroupCheckboxList from '../../pages/parts/_app-form-group-checkbox-list.jsx';
import {AsideLink} from '../aside-link/aside-link';

@observer
export class NewAppDetails extends React.Component {
  static propTypes = {
    masterStore: PropTypes.object.isRequired,
    disabled: PropTypes.bool
  }

  static defaultProps = {
    disabled: false
  }

  constructor(props) {
    super(props);
    this.masterStore = this.props.masterStore;
    this.newAppMasterStore = this.props.masterStore.newAppMasterStore;
    this.newAppDetailsStore = this.newAppMasterStore.newAppDetailsStore;
    this.references = {};
    this.downloadChecklist = this.masterStore.downloadDeveloperChecklistFile.bind(this.masterStore);

    reaction(
      () => this.newAppMasterStore.newAppValidationsStore.errorDestination,
      () => {this.placeFocus();}
    );
  }

  placeFocus = () => {
    this.newAppMasterStore.newAppValidationsStore.placeFocus({
      references: this.references,
      defaultFieldId: 'app_type'
    });
  }

  saveRef = (name, ref) => {
    this.references[name] = ref;
  }

  componentDidMount() {
    this.placeFocus();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.disabled === true && this.props.disabled === false) {
      // The user has edited step 0 and resumed at this step
      this.placeFocus();
    }
  }

  onChange = (name, newValue) => {
    this.newAppDetailsStore.handleInput(name, newValue);
  }

  onCheck = target => {
    if (!this.props.disabled) {
      this.newAppDetailsStore.handleCheck(target);
    }
  }

  onCustomDateChange = date => {
    this.newAppDetailsStore.handleCustomDateChange(date);
  }

  onDatepickerFocusChange = event => {
    this.newAppDetailsStore.handleDatepickerFocusChange(event.focused);
  }

  handleUpload = event => {
    this.newAppMasterStore.newAppUploadStore.handleDeveloperChecklistUpload(event);
  }

  handleRemoveFile = event => {
    this.newAppMasterStore.newAppUploadStore.removeDeveloperChecklist(event);
  }

  renderInput = ({name, className = '', type = 'text', onBlur}) => {
    const fieldInfo = this.newAppDetailsStore.inputFields[name];
    const helpText = fieldInfo.subtext && fieldInfo.subtext.join(' ');
    const errorText = fieldInfo.errorList.length > 0 ? fieldInfo.errorList.join(' ') : '';

    return (
      <TextInput
        id={name}
        type={type}
        ref={i => {this.references[name] = i;}}
        required={fieldInfo.required}
        charLimit={fieldInfo.validations.maxChars}
        labelText={fieldInfo.label}
        helperText={helpText}
        value={this.newAppMasterStore.newAppData[name]}
        updateValue={newValue => {this.onChange(name, newValue);}}
        onBlur={onBlur}
        format={fieldInfo.validations.format}
        errorMessage={errorText}
        className={className}
        disabled={this.props.disabled}
        showErrorDirectiveFromMaster={this.newAppMasterStore.masterStateProps.errorsAreDisplayed} />
    );
  }

  getReleaseDateState = () => {
    if (!this.newAppDetailsStore.releaseDateIsValid) {
      return 'has-error';
    } else {
      return '';
    }
  }

  renderReleaseDate = () => {
    const optionsList = [
      {
        title: 'Immediately upon approval',
        hiddenValue: 'IMMEDIATELY'
      },
      {
        title: 'Custom Date',
        hiddenValue: 'CUSTOM',
        additionalJsx: (
          <React.Fragment>
            <div className="help-text">
              Your app will be released after 3am EST on the date selected.
            </div>
          </React.Fragment>
        )
      }
    ];
    return (
      <React.Fragment>
      <RadioGroup
        id="release-date"
        key="release-date"
        ref={i => (this.references['releaseDate'] = i)}
        value={this.newAppDetailsStore.releaseDateType}
        updateValue={newValue => {this.newAppDetailsStore.handleReleaseDateChange(newValue);}}
        required={true}
        labelText="Scheduled release date"
        optionsList={optionsList}
        className={`release-date ${this.getReleaseDateState()}`}
        errorMessage={this.newAppMasterStore.REQUIRED_ERROR}
        disabled={this.props.disabled}
        showErrorDirectiveFromMaster={this.newAppMasterStore.masterStateProps.errorsAreDisplayed} />
        <div className="label-additional-block">
          <DatePicker
            id="custom-release-date"
            key="custom-release-date"
            handleOnChange={this.onCustomDateChange}
            disabled={!this.newAppDetailsStore.releaseDateType === 'CUSTOM' || this.props.disabled}
            uniqueSrText="Please choose a custom release date."
            utcOffset="-5:00"
            setHourTo={3}
            date={this.newAppMasterStore.newAppData.releaseDate} />
        </div>
      </React.Fragment>
    );
  }

  getAppTypeState = () => {
    if (!this.newAppDetailsStore.appTypeIsValid) {
      return 'has-error';
    } else {
      return '';
    }
  }

  renderAppType = () => {
    const selectedAppType = this.newAppMasterStore.newAppData.app_type;
    let optionsList = [
      {
        title: 'FirstNet Verified',
        additionalJsx: (
          <React.Fragment>
            <div className="help-text">
              FirstNet security review will be performed.
            </div>
            <div className="label-additional-block">
              {this.renderChecklistDownload('GENERAL', selectedAppType)}
            </div>
          </React.Fragment>
        ),
        hiddenValue: 'GENERAL'
      },
      {
        title: 'FirstNet Certified',
        additionalJsx: (
          <React.Fragment>
            <div className="help-text">
              Requires proof of 99.99% Service Level Availability. FirstNet
              security review will be performed.
            </div>
            <div className="label-additional-block">
              {this.renderChecklistDownload('CERTIFIED', selectedAppType)}
            </div>
          </React.Fragment>
        ),
        hiddenValue: 'CERTIFIED'
      }
    ];

    return (
      <RadioGroup
        id="app-approval-type"
        ref={i => (this.references['app_type'] = i)}
        value={selectedAppType}
        updateValue={newValue => {this.newAppDetailsStore.handleAppTypeChange(newValue);}}
        required={true}
        errorMessage={this.newAppMasterStore.REQUIRED_ERROR}
        labelText="App Approval Type"
        optionsList={optionsList}
        className={`app-approval-type ${this.getAppTypeState()}`}
        disabled={this.props.disabled}
        showErrorDirectiveFromMaster={this.newAppMasterStore.masterStateProps.errorsAreDisplayed} />
    );
  }

  renderChecklistDownload = (checklistType, appType) => {
    if (checklistType === appType) {
      let downloadLink = '',
        linkTitle = '';
      if (this.newAppDetailsStore.isCertifiedType) {
        downloadLink = config.devCertifiedChecklistForm;
        linkTitle = 'Certified App Checklist';
      } else if (this.newAppDetailsStore.isGeneralType) {
        downloadLink = config.devGeneralChecklistForm;
        linkTitle = 'Verified App Checklist';
      }

      return (
        <div className="checklist-download description outside-links">
          <h3 className="checklist-download-header">Required Documents:</h3>
          <ol>
            <li>
              <AsideLink
                to={downloadLink}
                text={linkTitle}
                srTextBefore="Download"
                srTextAfter=". Link will download a pdf file."
                iconAfterClass="icon-download" />
            </li>
            {this.newAppDetailsStore.isCertifiedType && (
              <li>
                Analyzed source code scan from an approved vendor.
                <div className="help-text">
                  Download list of approved vendors and specific requirements
                  for analyzing source code scans
                </div>
                <AsideLink
                  to={config.devSourceCodeScanInfo}
                  text="Source Code Scan Info"
                  srTextBefore="Download"
                  srTextAfter=". Link will download an excel file."
                  iconAfterClass="icon-download" />
              </li>
            )}
          </ol>
        </div>
      );
    }
    return '';
  }

  render() {
    return (
      <div className="new-app-details">
        <div className="container">
          <h2>App Details</h2>
          <div className="row">
            <form className="col-6">{this.renderAppType()}</form>
            <div className="col-5 offset-1 outside-links walkthrough-helpful-links">
              Want to know more about certification?
              <br />
              <AsideLink
                to={config.devCertificationDetails}
                text="FirstNet Certification Details" />
            </div>
          </div>
          <div className="row">
            <div className="col-6">
              <Dropzone
                saveRef={this.saveRef}
                disabled={this.props.disabled}
                targetFile={this.newAppMasterStore.newAppUploadStore.developerChecklist}
                onUpload={this.handleUpload}
                onDownload={this.downloadChecklist}
                onRemoveFile={this.handleRemoveFile}
                uploadDate={this.newAppMasterStore.newAppData.caFormUploadDate}
                label="Upload File(s)"
                
                errorText={this.newAppMasterStore.newAppUploadStore.certificationsDropzoneError}
                hasError={Boolean(this.newAppMasterStore.newAppUploadStore.certificationsDropzoneError)}
                isValid={this.newAppDetailsStore.developerChecklistIsValid}
              />
            </div>
          </div>
          <hr />
          <div className="row">
            <div className="col-6">
              <form>
                {this.renderInput({name: 'version', className: 'short'})}
                {this.renderReleaseDate()}
              </form>
            </div>

            <div className="col-5 offset-1 outside-links">
              Need additional help? Check out our:<br />
              <AsideLink isInternal={true} to="/faq" text="Relevant FAQs" />
            </div>
          </div>

          <div className="row">
            <div className="col-6">
              <form>
                {this.renderInput({name: 'shortDescription', type: 'textarea'})}
                {this.renderInput({name: 'longDescription', type: 'textarea'})}
                {this.renderInput({name: 'versionNotes', type: 'textarea'})}
                <AppFormCheckboxList
                  newAppMasterStore={this.newAppMasterStore}
                  type={'category'}
                  required={true}
                  saveRef={ref => this.saveRef('category', ref)} />
                <AppFormCheckboxList
                  newAppMasterStore={this.newAppMasterStore}
                  type={'segments'}
                  required={true}
                  saveRef={ref => this.saveRef('segments', ref)} />
                <AppFormCheckboxList
                  newAppMasterStore={this.newAppMasterStore}
                  saveRef={ref => this.saveRef('capabilities', ref)}
                  type={'capabilities'} />
                <AppFormCheckboxList
                  newAppMasterStore={this.newAppMasterStore}
                  saveRef={ref => this.saveRef('certifications', ref)}
                  type={'certifications'} />
                <AppFormGroupCheckboxList 
                  newAppMasterStore={this.newAppMasterStore} 
                  type={'searchTags'} 
                  group={'searchTagGroups'} 
                  required={true} 
                  saveRef={ref => this.saveRef('searchTags', ref)} />               
                {this.renderInput({name: 'tags', type: 'textarea'})}
                {this.renderInput({name: 'authorName'})}
                {this.renderInput({name: 'aboutDev', type: 'textarea'})}
                {this.renderInput({
                  name: 'devWebsite',
                  onBlur: () => {this.newAppDetailsStore.transformHyperlink('devWebsite');}
                })}
              </form>
            </div>

            <div className="col-5 offset-1 outside-links">
              Need help writing these descriptions?<br />
              <AsideLink to={config.devMarketingYourApp} text="Developer Portal: Marketing your App" />
            </div>
          </div>
        </div>
      </div>
    );
  }
}
